import React, { useEffect, useState } from 'react';
import { Accident } from '../types';
import { Circle, CheckCircle, Clock } from 'react-feather';
import './ReplacementLoadingScreen.css';

interface ReplacementLoadingScreenProps {
  accident: Accident;
  leadName: string;
  onRequestComplete: () => void;
}

// Move steps outside component to avoid recreation on each render
const LOADING_STEPS = [
  {
    id: 'processing',
    title: 'Processing your request',
    description: 'Analyzing accident details and location',
    duration: 2500
  },
  {
    id: 'matching',
    title: 'Finding available vehicles',
    description: 'Searching for replacement vehicles in your area',
    duration: 3000
  },
  {
    id: 'dispatching',
    title: 'Dispatching vehicle',
    description: 'Coordinating with our dispatch center',
    duration: 2500
  },
  {
    id: 'confirmed',
    title: 'Vehicle dispatched!',
    description: 'Your replacement vehicle is on the way',
    duration: 2000
  }
];

export const ReplacementLoadingScreen: React.FC<ReplacementLoadingScreenProps> = ({
  accident,
  leadName,
  onRequestComplete
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Hide header when this component is active
  useEffect(() => {
    document.body.classList.add('replacement-loading-active');
    return () => {
      document.body.classList.remove('replacement-loading-active');
    };
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (currentStep < LOADING_STEPS.length - 1) {
      timer = setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, LOADING_STEPS[currentStep].duration);
    } else if (currentStep === LOADING_STEPS.length - 1) {
      timer = setTimeout(() => {
        setIsComplete(true);
        setShowSuccess(true);
      }, LOADING_STEPS[currentStep].duration);
    }

    return () => clearTimeout(timer);
  }, [currentStep]);

  const currentStepData = LOADING_STEPS[currentStep];

  return (
    <div className="replacement-loading-modal">
      <div className="modal-backdrop" />
      
      <div className="modal-content">
        <div className="loading-center">
          {/* Main Animation Section */}
          <div className="main-animation">
            {isComplete && showSuccess ? (
              <div className="success-animation">
                <div className="success-circle">
                  <CheckCircle size={48} className="success-icon" />
                </div>
              </div>
            ) : (
              <div className="processing-animation">
                <div className="car-container">
                  <div className="animated-car">
                    <Circle size={40} className="car-icon" />
                  </div>
                  <div className="pulse-ring"></div>
                  <div className="pulse-ring delay-1"></div>
                  <div className="pulse-ring delay-2"></div>
                </div>
              </div>
            )}
          </div>

          {/* Title and Description */}
          <div className="loading-text">
            <h1 className={`main-title ${isComplete ? 'success' : ''}`}>
              {isComplete ? 'Request Confirmed!' : 'Requesting replacement vehicle'}
            </h1>
            
            <p className="main-subtitle">
              {isComplete 
                ? `Hi ${leadName}, your replacement vehicle has been dispatched`
                : currentStepData.description
              }
            </p>
          </div>

          {/* Progress Steps */}
          <div className="progress-container">
            <div className="progress-track">
              <div className="loading-progress-bar">
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${((currentStep + 1) / LOADING_STEPS.length) * 100}%`,
                    transition: 'width 0.8s ease-in-out'
                  }}
                />
              </div>
              
              <div className="step-indicators">
                {LOADING_STEPS.map((step, index) => (
                  <div 
                    key={step.id}
                    className={`step-indicator-item ${
                      index <= currentStep ? 'completed' : 'pending'
                    }`}
                  >
                    <div 
                      className={`step-dot ${
                        index <= currentStep ? 'completed' : 'pending'
                      }`}
                    >
                      {index <= currentStep ? (
                        <div className="dot-check">✓</div>
                      ) : (
                        <div className="dot-number">{index + 1}</div>
                      )}
                    </div>
                    <div className="step-label">
                      {step.title.replace('your request', 'request').replace('available vehicles', 'vehicles')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Current Step Title */}
          {!isComplete && (
            <div className="current-step">
              <span className="step-title">{currentStepData.title}</span>
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          {/* Accident Reference */}
          <div className="accident-reference-card">
            <div className="reference-icon">
              <Clock size={14} />
            </div>
            <div className="reference-content">
              <span className="reference-label">Request for:</span>
              <span className="reference-title">{accident.title}</span>
              <span className="reference-location">{accident.location.address}</span>
            </div>
          </div>

          {/* Success Message */}
          {isComplete && showSuccess && (
            <div className="success-details">
              <div className="success-card">
                <h3>What happens next?</h3>
                <div className="success-steps">
                  <div className="success-step">
                    <span className="success-check">✓</span>
                    <span>Confirmation email sent to customer</span>
                  </div>
                  <div className="success-step">
                    <span className="success-check">✓</span>
                    <span>The Dispatch team may contact you</span>
                  </div>
                  <div className="success-step">
                    <span className="success-check">✓</span>
                    <span>Real-time vehicle tracking available</span>
                  </div>
                </div>
              </div>
              
              {/* Done Button */}
              <button 
                className="done-button"
                onClick={onRequestComplete}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 